import { motion } from 'framer-motion';
import { Trees, Recycle, Droplets, Sun, Leaf, Wind, ArrowUpRight } from 'lucide-react';
import { useEnquiry } from '../context/EnquiryContext';

const PILLARS = [
  { num: '01', icon: Trees, title: 'Avenue Plantations & Parks', desc: 'Every Prakruti layout is planted before it is sold — tree-lined avenues, landscape gardens and walking tracks that shade the roads from day one.' },
  { num: '02', icon: Recycle, title: 'Sewage Treatment & Waste', desc: 'On-site sewage treatment plants and clean drainage systems keep greywater out of the soil and ready for garden reuse.' },
  { num: '03', icon: Droplets, title: 'Water Security', desc: 'Underground drainage, rainwater-harvesting-ready storm channels and an individual water connection to every single plot.' },
  { num: '04', icon: Sun, title: 'Solar-Ready Layouts', desc: 'Shadow-planned plots and our upcoming solar division mean every home built here can generate its own power.' },
  { num: '05', icon: Leaf, title: 'Green Materials', desc: 'Our material supply arm prioritises certified, low-impact stock — fly-ash blocks, low-VOC finishes and responsibly sourced steel.' },
  { num: '06', icon: Wind, title: 'Climate-Responsive Design', desc: 'Orientation, ventilation and deep setbacks planned for the Deccan heat — homes that stay cool before the AC is ever switched on.' },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay },
});

export default function Sustainability() {
  const { open } = useEnquiry();
  return (
    <main data-testid="sustainability-page">
      <section className="pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">SUSTAINABILITY — BUILD TODAY, SUSTAIN TOMORROW</p>
          <motion.h1
            initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink max-w-4xl"
            data-testid="sustainability-headline"
          >
            Green is not a feature.<br />
            <span className="font-serif italic font-medium text-terra">It is the plan.</span>
          </motion.h1>
          <motion.p {...fade(0.15)} className="mt-8 text-base lg:text-lg text-ink/60 leading-relaxed max-w-2xl">
            Long before a family builds on a Prakruti plot, the trees are already growing, the water lines are already laid,
            and the sun has already been accounted for. This is how we approach every square foot.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="sustainability-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 border-t border-clay">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.num} {...fade(i * 0.05)}
              className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center py-10 border-b border-clay"
              data-testid={`pillar-${p.num}`}
            >
              <div className="md:col-span-2 flex items-center gap-4">
                <span className="font-serif italic text-5xl lg:text-6xl text-ink/15 group-hover:text-terra transition-colors duration-500 leading-none">{p.num}</span>
              </div>
              <div className="md:col-span-1">
                <span className="w-11 h-11 bg-terra/10 text-terra flex items-center justify-center"><p.icon size={18} /></span>
              </div>
              <h3 className="md:col-span-4 font-heading font-bold text-xl lg:text-2xl tracking-tight text-ink">{p.title}</h3>
              <p className="md:col-span-5 text-sm lg:text-base leading-relaxed text-ink/60">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-moss text-sand" data-testid="sustainability-solar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">THE SOLAR COMMITMENT</p>
            <motion.h2 {...fade(0)} className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl leading-tight">
              Every roof we hand over can <span className="font-serif italic font-medium text-terra">power itself</span>
            </motion.h2>
            <motion.p {...fade(0.1)} className="mt-6 text-sand/60 leading-relaxed max-w-lg">
              Our renewable energy division brings rooftop solar installation, panel supply and EPC services under the same
              roof as our construction work — cutting power bills and carbon in one move.
            </motion.p>
            <motion.button {...fade(0.15)} onClick={() => open()} data-testid="sustainability-enquire-btn"
              className="mt-9 bg-terra text-white text-[11px] font-medium tracking-[0.2em] px-10 py-4 flex items-center gap-2 hover:bg-sand hover:text-ink transition-colors duration-300">
              BUILD GREEN WITH US <ArrowUpRight size={14} />
            </motion.button>
          </div>
          <motion.div {...fade(0.1)} className="grid grid-cols-2 gap-px bg-sand/10 border border-sand/10">
            {[['Trees planted per layout avenue', '100s'], ['Plots with UGD + water line', '100%'], ['Layouts designed solar-ready', 'All 3'], ['Green-certified material stock', 'Only']].map(([label, val]) => (
              <div key={label} className="bg-moss p-8">
                <p className="font-heading font-extrabold tracking-tighter text-4xl text-terra">{val}</p>
                <p className="mt-2 text-xs text-sand/50 leading-relaxed">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
}
